export type SignalLevel = 'stable' | 'tracking' | 'elevated' | 'strong';
export type ValidityLabel = 'high' | 'moderate' | 'low';
export type EchoDirection = 'forward' | 'backward';

export interface ReactionTrial {
  trial: number;
  rt: number;
  hit: boolean;
}

export interface ReactionResult {
  trials: ReactionTrial[];
  median: number;
  mean: number;
  sd: number;
  cv: number;
  lapses: number;
  maxStreak: number;
  /** stimuli thrown away because the page was hidden mid-trial (brief 3.3) */
  interruptions?: number;
}

export interface WordFindingResult {
  category: string;
  words: string[];
  timestamps: number[];
  uniqueValid: number;
  repetitions: number;
  firstLatency: number;
  meanInterval: number;
  clustering: number;
}

export interface EchoTrialResult {
  length: number;
  correct: boolean;
}

export interface DigitSpanResult {
  forwardTrials: EchoTrialResult[];
  backwardTrials: EchoTrialResult[];
  maxForward: number;
  maxBackward: number;
  fwdAccuracy: number;
  bwdAccuracy: number;
  backwardCost: number;
}

export interface RecallItems {
  name: string;
  color: string;
  place: string;
  object: string;
}

export interface RecallResponses {
  name: string;
  color: string;
  object: string;
  place: string;
}

export interface DelayedRecallResult {
  recallItems: RecallItems;
  freeResponses: RecallResponses;
  cuedResponses: Partial<RecallResponses>;
  freeScore: number;
  cuedScore: number;
  cueBenefit: number;
  // Step2 change5: which item fields were recalled freely vs only with a cue,
  // persisted at test time so the results screen can name the exact items
  // (the name / the colour / …) without re-deriving fuzzy matches. Missed =
  // the four fields minus these two sets.
  freeItems?: string[];
  cuedItems?: string[];
}

export interface SwitchTrial {
  shape: string;
  color: { name: string; hex: string };
  rule: 'color' | 'shape';
  bins: any[];
  correctIdx: number;
  isSwitch: boolean;
  response: number | null;
  correct: boolean | null;
  rt: number;
}

export interface ShapeSorterResult {
  trials: SwitchTrial[];
  accuracy: number;
  switchCost: number;
  perseverations: number;
}

export interface VigilanceTrial {
  isTarget: boolean;
  responded: boolean;
  rt: number;
  correct: boolean | null;
}

export interface VigilancePulseResult {
  trials: VigilanceTrial[];
  hitRate: number;
  falseAlarms: number;
  omissions: number;
  rtDrift: number;
}

export interface ValidityResult {
  flags: string[];
  label: ValidityLabel;
}

export interface SignalsResult {
  retrieval: SignalLevel;
  workingMemory: SignalLevel;
  processingSpeed: SignalLevel;
  delayedRecall: SignalLevel;
  flags: string[];
}

export interface ProfileResult {
  name: string;
  description: string;
}

export const WORD_LISTS: Record<string, Set<string>> = {
  Animals: new Set([
    'cat','dog','horse','cow','pig','sheep','goat','chicken','duck','turkey','rabbit','hamster','mouse','rat','fish','goldfish','shark','whale','dolphin','octopus','squid','crab','lobster','shrimp','clam','oyster','starfish','jellyfish','seal','walrus','otter','beaver','bear','panda','koala','kangaroo','lion','tiger','leopard','cheetah','jaguar','panther','cougar','wolf','fox','coyote','hyena','elephant','giraffe','zebra','rhino','rhinoceros','hippo','hippopotamus','gorilla','chimpanzee','chimp','monkey','orangutan','baboon','deer','moose','elk','caribou','antelope','gazelle','buffalo','bison','camel','llama','alpaca','donkey','mule','pony','eagle','hawk','falcon','owl','parrot','penguin','flamingo','crane','heron','pelican','seagull','gull','pigeon','dove','crow','raven','robin','sparrow','cardinal','woodpecker','hummingbird','swan','goose','peacock','toucan','vulture','ostrich','emu','snake','lizard','gecko','iguana','chameleon','crocodile','alligator','turtle','tortoise','frog','toad','salamander','newt','butterfly','bee','wasp','ant','spider','scorpion','beetle','ladybug','dragonfly','mosquito','fly','cricket','grasshopper','caterpillar','moth','worm','snail','slug','bat','hedgehog','squirrel','raccoon','skunk','badger','ferret','mink','weasel','porcupine','armadillo','possum','opossum','mole','chinchilla'
  ]),
  Food: new Set([
    'apple','banana','orange','grape','strawberry','blueberry','raspberry','cherry','peach','pear','plum','mango','pineapple','watermelon','melon','cantaloupe','kiwi','lemon','lime','grapefruit','coconut','avocado','tomato','potato','carrot','broccoli','cauliflower','spinach','lettuce','cabbage','celery','cucumber','onion','garlic','pepper','corn','peas','beans','rice','pasta','noodles','bread','toast','bagel','muffin','croissant','pancake','waffle','cereal','oatmeal','egg','eggs','cheese','butter','milk','yogurt','cream','chicken','beef','pork','lamb','turkey','fish','salmon','tuna','shrimp','steak','bacon','sausage','ham','pizza','burger','hamburger','sandwich','salad','soup','stew','chili','taco','burrito','sushi','curry','pie','cake','cookie','brownie','donut','doughnut','chocolate','candy','ice cream','pudding','jam','honey','sugar','salt','flour','oil','vinegar','mustard','ketchup','mayo','mayonnaise','sauce','nuts','almond','almonds','walnut','walnuts','peanut','peanuts','cashew','cashews','pistachio','pistachios','olive','olives','pickle','pickles','mushroom','mushrooms','zucchini','eggplant','beet','beets','radish','turnip','squash','pumpkin','artichoke','asparagus','leek','kale','arugula','parsley','cilantro','basil','oregano','thyme','rosemary','mint','dill','ginger','turmeric','cinnamon','nutmeg','paprika','cumin','tofu','hummus','falafel','pretzel','popcorn','cracker','crackers','chips','fries','hot dog','ribs','roast','jerky','pepperoni','salami','mozzarella','parmesan','cheddar','feta','ricotta','granola','smoothie','juice','lemonade','tea','coffee','espresso','latte','cocoa','milkshake','soda','cola','water','wine','beer','ramen','risotto','lasagna','ravioli','spaghetti','macaroni','couscous','quinoa','oats','wheat','biscuit','scone','eclair','macaron','caramel','toffee','marshmallow','gelato','mousse','tiramisu','cheesecake','tart','quiche','omelet','crepe'
  ]),
  'Household Objects': new Set([
    'chair','table','desk','couch','sofa','bed','pillow','blanket','sheet','mattress','lamp','light','candle','mirror','clock','watch','phone','television','tv','remote','computer','laptop','tablet','keyboard','speaker','radio','fan','heater','oven','stove','microwave','fridge','refrigerator','freezer','dishwasher','washer','dryer','toaster','blender','mixer','kettle','pot','pan','plate','bowl','cup','mug','glass','fork','knife','spoon','napkin','towel','soap','shampoo','brush','comb','toothbrush','toothpaste','razor','scissors','broom','mop','vacuum','bucket','sponge','basket','shelf','bookshelf','drawer','cabinet','closet','hanger','curtain','curtains','rug','carpet','door','window','key','keys','lock','handle','nail','screw','hammer','screwdriver','tape','glue','rope','wire','battery','plug','switch','iron','pin','needle','thread','pen','pencil','eraser','paper','notebook','envelope','box','bag','jar','bottle','container','lid','tray','rack','hook','ring','vase','frame','picture','calendar','thermometer','scale','umbrella','trash can','bin','plunger','wrench','pliers','drill','ruler','flashlight','lantern','lighter','coaster','tablecloth','stapler','clipboard','calculator','printer','monitor','mouse','charger','headphones','alarm','thermostat','shower','bathtub','toilet','sink','faucet','shovel','rake','ladder','stool','bench','ottoman','recliner','crib','nightstand','dresser','wardrobe','chest','coffee table','chandelier','ceiling fan','humidifier','air purifier','dishcloth','cutting board','whisk','spatula','ladle','tongs','peeler','grater','rolling pin','can opener','corkscrew','pitcher','teapot','coffee maker','waffle iron','wok','skillet','saucepan'
  ]),
};

// Hebrew word lists — same category keys as WORD_LISTS. Used when the active
// language is Hebrew so speech/typed Hebrew words validate (speech recognition
// runs in he-IL and returns Hebrew text, which can never match the English sets).
export const WORD_LISTS_HE: Record<string, Set<string>> = {
  Animals: new Set([
    'כלב','חתול','סוס','פרה','חזיר','כבש','עז','תרנגול','תרנגולת','ברווז','אווז','הודו','אריה','נמר','נמרה','פיל','גירפה','זברה','קוף','גורילה','שימפנזה','דוב','פנדה','קואלה','קנגורו','זאב','שועל','צבי','איל','גמל','חמור','ארנב','ארנבת','עכבר','חולדה','אוגר','סנאי','קיפוד','דג','כריש','לוויתן','דולפין','תמנון','דיונון','סרטן','מדוזה','צב','נחש','לטאה','זיקית','תנין','צפרדע','קרפדה','נשר','בז','עיט','ינשוף','תוכי','פינגווין','פלמינגו','יונה','עורב','ציפור','דרור','ברבור','טווס','יתוש','זבוב','דבורה','צרעה','נמלה','עכביש','עקרב','חיפושית','פרפר','חרק','תולעת','חילזון','שבלול','עטלף','קרנף','היפופוטם','לוטרה','דביבון','שפן','תן','צבוע','תאו','ביזון','יען','דרבן','נמייה','סמור','חדף','אורקה','צלופח','קרפיון','סלמנדרה','חרגול','צרצר','שפירית','נחליאלי','דוכיפת','חסידה','שחף','עגור','אנפה','דוב קוטב','יחמור','ראם','עז בר','יעל','חזיר בר'
  ]),
  Food: new Set([
    'תפוח','בננה','תפוז','ענב','ענבים','תות','אוכמניות','דובדבן','אפרסק','אגס','שזיף','מנגו','אננס','אבטיח','מלון','קיווי','לימון','ליים','אשכולית','קוקוס','אבוקדו','עגבניה','עגבנייה','גזר','ברוקולי','כרובית','תרד','חסה','כרוב','סלרי','מלפפון','בצל','שום','פלפל','תירס','אפונה','שעועית','אורז','פסטה','אטריות','לחם','טוסט','בייגל','מאפין','קרואסון','פנקייק','וופל','דייסה','ביצה','ביצים','גבינה','חמאה','חלב','יוגורט','שמנת','עוף','בקר','כבש','דג','סלמון','טונה','שרימפס','סטייק','נקניק','נקניקיה','פיצה','המבורגר','כריך','סלט','מרק','טאקו','סושי','קארי','עוגה','עוגיה','עוגייה','סופגניה','שוקולד','סוכריה','גלידה','ריבה','דבש','סוכר','מלח','קמח','שמן','חומוס','פלאפל','פופקורן','קרקר','ביסקוויט','תה','קפה','מיץ','לימונדה','מים','יין','בירה','פיתה','בורקס','שניצל','אגוז','אגוזים','בוטן','בוטנים','שקד','שקדים','זית','זיתים','אפרסמון','רימון','תאנה','תמר','משמש','נקטרינה','קלמנטינה','פומלה','פפאיה','דלעת','קישוא','במיה','חציל','סלק','צנון','לפת','כרישה','בטטה','עדשים','קינואה','בורגול','כוסמת','טחינה','חלבה','קוסקוס','שקשוקה','סביח','שווארמה','קבב','צימוקים','חמוציות','גרעינים'
  ]),
  'Household Objects': new Set([
    'כיסא','כסא','שולחן','ספה','מיטה','כרית','שמיכה','סדין','מזרן','מנורה','נר','מראה','שעון','טלפון','טלוויזיה','שלט','מחשב','מקלדת','רמקול','רדיו','מאוורר','תנור','כיריים','מיקרוגל','מקרר','מקפיא','מדיח','מייבש','טוסטר','בלנדר','מיקסר','קומקום','סיר','מחבת','צלחת','קערה','כוס','ספל','מזלג','סכין','כף','כפית','מפית','מגבת','סבון','שמפו','מברשת','מסרק','מספריים','מטאטא','מגב','שואב','דלי','ספוג','סל','מדף','ארון','מגירה','קולב','וילון','שטיח','מרבד','דלת','חלון','מפתח','מנעול','ידית','מסמר','בורג','פטיש','מברג','דבק','חבל','סוללה','תקע','מתג','מגהץ','סיכה','מחט','חוט','עט','עיפרון','מחק','נייר','מחברת','מעטפה','קופסה','קופסא','תיק','צנצנת','בקבוק','מסגרת','תמונה','אגרטל','פנס','סולם','שרפרף','ספסל','כורסה','כורסא','שטיחון','וו','מדפסת','מסך','עכבר','מטען','אוזניות','שידה','כוננית','מזנון','מתלה','כיור','ברז','אסלה','אמבטיה','מקלחת','תריס','מאפרה','פמוט','נברשת','מזגן','מצקת','מערוך','פומפייה','מסננת','קולפן','פותחן','תבנית','קנקן','מגש','פח','יעה','סמרטוט','מטלית','מטרייה'
  ]),
};

export const ANIMAL_CLUSTERS: Record<string, string[]> = {
  farm: ['cow','pig','sheep','goat','chicken','duck','turkey','horse','donkey','mule'],
  pet: ['cat','dog','hamster','rabbit','goldfish','parrot','ferret'],
  africa: ['lion','elephant','giraffe','zebra','rhino','hippo','gorilla','chimpanzee','hyena','cheetah','leopard','gazelle'],
  ocean: ['whale','dolphin','shark','octopus','squid','crab','lobster','shrimp','jellyfish','seal','walrus','starfish'],
  bird: ['eagle','hawk','owl','parrot','penguin','flamingo','crow','robin','sparrow','swan','goose','pigeon','dove'],
  reptile: ['snake','lizard','turtle','tortoise','crocodile','alligator','gecko','iguana','chameleon'],
  insect: ['butterfly','bee','ant','spider','beetle','mosquito','fly','cricket','grasshopper','moth','dragonfly'],
  rodent: ['mouse','rat','squirrel','hamster','beaver','porcupine'],
};

export const RECALL_POOLS: Record<keyof RecallItems, string[]> = {
  name: ['Sarah','David','Rachel','Michael','Emma','James','Lisa','Robert','Anna','Thomas'],
  // AA colour set only (handoff 2026-08-17): yellow -> brown, pink/silver dropped.
  color: ['orange','purple','brown','green','red','blue'],
  // 'hospital' intentionally excluded: a charged setting for a menopause
  // wellness app (handoff 2026-08-17, part B recall material).
  place: ['garden','kitchen','library','park','beach','station','market'],
  object: ['umbrella','notebook','clock','basket','ring','envelope','candle','scarf'],
};

// Handoff 2026-08-17 (part B, recall material): every colour is rendered AS
// coloured TEXT (the colour word in the encode sentence), so each must pass AA
// against white (>=4.5:1). The SAME hex backs the retrieval swatch, otherwise the
// swatch would encode a different shade than the word she read. Yellow (1.72:1)
// cannot be saved and is replaced by brown. pink/silver are kept only so legacy
// scans that stored them still resolve a colour; they are not in the recall pool.
export const COLORS_MAP: Record<string, string> = {
  orange: '#B4610F',
  purple: '#7A4FBE',
  brown: '#7C4A21',
  green: '#1F7A50',
  red: '#C0392B',
  blue: '#3E6FD9',
  yellow: '#d9c44a',
  pink: 'var(--brand-color)',
  silver: '#a0a8b0',
};

export const SC_SHAPES = ['circle', 'triangle', 'square', 'diamond', 'star'] as const;
export type SCShape = typeof SC_SHAPES[number];

export const SC_COLORS: { name: string; hex: string }[] = [
  { name: 'pink', hex: 'var(--brand-color)' },
  { name: 'blue', hex: '#4a90d9' },
  { name: 'green', hex: '#3a9e95' },
  { name: 'orange', hex: '#e88d67' },
];
